import {
  type EnvironmentId,
  type ResolvedKeybindingsConfig,
  type ScopedThreadRef,
  type ThreadId,
} from "@cmmd-forge/contracts";
import { MAX_TERMINALS_PER_GROUP, type Project, type Thread } from "../../types";
import { type ThreadTerminalState } from "../../terminalStateStore";
import { LastInvokedScriptByProjectSchema } from "../ChatView.logic";
import { useChatKeyboardShortcuts } from "./useChatKeyboardShortcuts";
import { useChatTerminalControls } from "./useChatTerminalControls";
import { type ServerThreadStatus } from "./serverThreadStatus";

type LastInvokedScriptByProject = typeof LastInvokedScriptByProjectSchema.Type;

export function useChatTerminalController(input: {
  readonly environmentId: EnvironmentId;
  readonly routeThreadRef: ScopedThreadRef;
  readonly serverThreadStatus: ServerThreadStatus;
  readonly activeThreadId: ThreadId | null;
  readonly activeThreadRef: ScopedThreadRef | null;
  readonly activeThreadKey: string | null;
  readonly activeThread: Thread | undefined;
  readonly activeProject: Project | null;
  readonly activeProjectCwd: string | null;
  readonly activeThreadWorktreePath: string | null;
  readonly gitCwd: string | null;
  readonly terminalState: ThreadTerminalState;
  readonly keybindings: ResolvedKeybindingsConfig;
  readonly canvasOpen: boolean;
  readonly focusComposer: () => void;
  readonly setThreadError: (threadId: ThreadId | null, error: string | null) => void;
  readonly setLastInvokedScriptByProjectId: (
    value:
      | LastInvokedScriptByProject
      | ((current: LastInvokedScriptByProject) => LastInvokedScriptByProject),
  ) => void;
  readonly onToggleDiff: () => void;
  readonly onToggleCanvas: (nextOpen: boolean) => void;
  readonly onTogglePreview: () => void;
  readonly togglePlanSidebar: () => void;
}) {
  const terminal = useChatTerminalControls({
    ...input,
    hasReachedSplitLimit: useHasReachedSplitLimit(input.terminalState),
    setLastInvokedScriptByProjectId: (updater) => {
      input.setLastInvokedScriptByProjectId((current) => updater(current));
    },
  });

  useChatKeyboardShortcuts({
    activeThreadId: input.activeThreadId,
    activeProject: input.activeProject,
    terminalOpen: Boolean(input.terminalState.terminalOpen),
    activeTerminalId: input.terminalState.activeTerminalId,
    keybindings: input.keybindings,
    setTerminalOpen: terminal.setTerminalOpen,
    toggleTerminalVisibility: terminal.toggleTerminalVisibility,
    splitTerminal: terminal.splitTerminal,
    createNewTerminal: terminal.createNewTerminal,
    closeTerminal: terminal.closeTerminal,
    runProjectScript: terminal.runProjectScript,
    onToggleDiff: input.onToggleDiff,
    onToggleCanvas: input.onToggleCanvas,
    onTogglePreview: input.onTogglePreview,
    canvasOpen: input.canvasOpen,
    togglePlanSidebar: input.togglePlanSidebar,
  });

  return terminal;
}

function useHasReachedSplitLimit(terminalState: ThreadTerminalState): boolean {
  const activeTerminalGroup =
    terminalState.terminalGroups.find(
      (group) => group.id === terminalState.activeTerminalGroupId,
    ) ??
    terminalState.terminalGroups.find((group) =>
      group.terminalIds.includes(terminalState.activeTerminalId),
    ) ??
    null;
  return (activeTerminalGroup?.terminalIds.length ?? 0) >= MAX_TERMINALS_PER_GROUP;
}
